/**
 * Identity-scoped localStorage helpers.
 *
 * Any storage that holds *user data* (open windows, installed apps, drafts,
 * filesystem, app state, preferences) must be scoped per identity so a guest
 * session and a logged-in session — or two different logged-in users on the
 * same device — never read or write each other's blobs.
 *
 * This app has no auth — there is a single, permanent guest identity — so the
 * scope suffix is always `g`. The scoping indirection is kept (rather than
 * hardcoding keys) so per-identity scoping can be reintroduced later by making
 * `getStorageScope()` return `u:<userId>` again.
 */

/**
 * Suffix appended to every scoped key. Always `g` (guest) in this build.
 */
export function getStorageScope(): string {
  return "g";
}

/**
 * Aliases for keys that were renamed during the scoping sweep. On first
 * guest read of `newName`, we also look for any of the listed legacy
 * names and migrate them. Without this, renamed keys appear wiped on
 * existing devices.
 */
const LEGACY_ALIASES: Record<string, string[]> = {
  "desktop-pets": ["desktop-pets-guest"],
  "desktop-pets-visible": ["desktop-pets-guest-visible"],
  "desktop-plants": ["desktop-plants-guest"],
  "desktop-plants-visible": ["desktop-plants-guest-visible"],
};

/**
 * Build a scope-suffixed storage key. Use this for any per-user data.
 *
 * On first guest read of a key whose legacy unscoped value still exists,
 * we transparently migrate it: copy `baseKey` (or any alias listed in
 * `LEGACY_ALIASES`) -> `baseKey::g` then remove the legacy key. Existing
 * devices keep their guest data; logged-in users get a clean slate.
 *
 * @example
 *   const key = scopedKey("desktopUI:windowState"); // -> "desktopUI:windowState::g"
 */
export function scopedKey(baseKey: string): string {
  const scope = getStorageScope();
  if (scope === "g") migrateLegacyKey(baseKey);
  return `${baseKey}::${scope}`;
}

const migrated = new Set<string>();

function migrateLegacyKey(baseKey: string): void {
  if (migrated.has(baseKey)) return;
  migrated.add(baseKey);
  const candidates = [baseKey, ...(LEGACY_ALIASES[baseKey] ?? [])];
  const scopedTarget = `${baseKey}::g`;
  for (const legacy of candidates) {
    migrateOne(legacy, scopedTarget, "local");
    migrateOne(legacy, scopedTarget, "session");
  }
}

function migrateOne(
  legacyKey: string,
  scopedTarget: string,
  kind: "local" | "session"
): void {
  try {
    const store = kind === "local" ? localStorage : sessionStorage;
    if (typeof store === "undefined") return;
    const value = store.getItem(legacyKey);
    if (value === null) return;
    if (store.getItem(scopedTarget) === null) {
      store.setItem(scopedTarget, value);
    }
    store.removeItem(legacyKey);
  } catch {
    // storage disabled / quota exceeded — ignore
  }
}
